import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { actions } from '@/store';
import validator from '@/constant/valitator';
import useSelectorUser from '@/hooks/use-selector-user';
import Message from '@/constant/message';
import { EAuthModalPage } from '@/store/types';
import postCheckEmailStatus from '@/fetches/user/auth/post-check-email-status';
import useAlert from '@/hooks/use-alert';
import theme from '@/styles/theme';
import media from '@/styles/media';
import postLoginBySocial from '@/fetches/user/auth/post-login-with-social';
import { ButtonLogo, RoundButton } from '../common/auth-button';
import { Container, ContentDiv, Wrapper } from '../common/new-auth-container';
import { BasicButton, BasicButtonLabel, ButtonDiv } from '../common/new-auth-button';
import { Input } from '../common/new-auth-input';
import { Title } from '../common/new-auth-text';
import { BalloonModal } from '../common/balloon-modal';
import {
  Line, MobileSocialLoginTitle, SimpleLoginTitle,
} from '../input-email';
import MobileLogo from '../common/auth-layout/mobile-logo';

export const SignUp = () => {
  const [name, setName] = useState<string>('');
  const [id, setId] = useState<string>('');
  const [pw, setPw] = useState<string>('');
  const [confirmPw, setConfirmPw] = useState<string>('');

  const [isName, setIsName] = useState<boolean>();
  const [isId, setIsId] = useState<boolean>();
  const [isPw, setIsPw] = useState<boolean>();
  const [isConfirm, setIsConfirm] = useState<boolean>();

  const [nameModal, setNameModal] = useState<string>('');
  const [idModal, setIdModal] = useState<string>('');
  const [pwModal, setPwModal] = useState<string>('');

  const [isValid, setIsValid] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { register, gotoAuthLogin, gotoAuthReset } = useSelectorUser('authModal');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { alert } = useAlert();

  const validateName = useCallback((value: string) => {
    const { err, args } = validator.checkName(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangeNameInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateName(value);
    setName(value);

    if (value.length > 0) {
      setIsName(valid === '');
    } else {
      setIsName(undefined);
    }

    if (valid !== '') {
      setNameModal(valid);
    }
  }, [validateName]);

  const onFocusOutName = useCallback(() => {
    setNameModal('');
  }, []);

  const onFocusInName = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validateName(value);
    if (valid !== '') {
      setNameModal(valid);
    }
  }, [validateName]);

  const onChangeIdInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = t(validator.checkEmail(value));
    setId(value);
    if (value.length >= 3) {
      setIsId(valid === '');
      if (valid !== '') {
        setIdModal(valid);
      }
    } else if (value.length < 1) {
      setIdModal('');
      setIsId(undefined);
    }
  }, [t]);

  const onFocusOutId = useCallback(() => {
    setIdModal('');
  }, []);

  const onFocusInId = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = t(validator.checkEmail(value));
    if (valid !== '') {
      setIdModal(valid);
    }
  }, [t]);

  const validatePassword = useCallback((value: string) => {
    const { err, args } = validator.checkPassword(value);
    if (err) {
      if (args?.value) {
        return t(err, { value: args.value }) as string;
      }
      if (args?.valueKeys) {
        return t(err, { value: args.valueKeys.map((v) => t(v)).join('\n') }) as string;
      }
      return t(err) as string;
    }
    return '';
  }, [t]);

  const onChangePwInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validatePassword(value);
    setPw(value);
    if (value.length >= 3) {
      setIsPw(valid === '');
      if (valid !== '') {
        setPwModal(valid);
      }
    } else if (value.length < 1) {
      setPwModal('');
      setIsPw(undefined);
    }
  }, [validatePassword]);

  const onFocusOutPw = useCallback(() => {
    setPwModal('');
  }, []);

  const onFocusInPw = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    const valid = validatePassword(value);
    if (valid !== '') {
      setPwModal(valid);
    }
  }, [validatePassword]);

  const onChangeConfirmInput = useCallback((event:React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > 0) {
      setConfirmPw(value);
      setIsConfirm(pw === value);
    } else {
      setIsConfirm(undefined);
    }
  }, [pw]);

  const gotoRegister = useCallback(() => {
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SELECT_USER));
  }, [dispatch]);

  const gotoCreator = useCallback(() => {
    dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.CREATOR_SIGN_UP));
  }, [dispatch]);

  const gotoLogin = useCallback(() => {
    if (gotoAuthLogin) {
      gotoAuthLogin();
    } else {
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.INPUT_EMAIL));
    }
  }, [dispatch, gotoAuthLogin]);

  useEffect(() => {
    const check = name.length > 0 && name.length <= 10 && isPw === true && isId === true && pw === confirmPw;
    setIsValid(check);
  }, [confirmPw, isId, isPw, pw, name.length]);

  /** 회원가입에서도 이메일 중복을 확인한다 */
  const onClickSignup = useCallback(async () => {
    setIsLoading(true);
    const result = await postCheckEmailStatus(id);
    switch (result) {
      case 'email exists with password':
        await alert(t(Message.이미사용중인이메일입니다다른이메일을입력하세요));
        break;
      case 'email exists without password':
        await alert(t(Message.비밀번호를설정해주세요)); // TODO: 추후 워딩 변경 가능 -> 이미 가입된 이메일입니다. 비밀번호를 설정해주세요.
        dispatch(actions.user.authModal.setEmail(id));
        if (gotoAuthReset) { gotoAuthReset(); }
        break;
      case 'email does not exist':
        dispatch(actions.user.authModal.setName(name));
        dispatch(actions.user.authModal.setEmail(id));
        dispatch(actions.user.authModal.setPassword(pw));
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.AUTH_EMAIL));
        break;
      default:
        await alert(t(Message.처리도중서버에서오류가발생했습니다잠시후다시시도하시거나관리자에게문의해주세요));
        break;
    }
    setIsLoading(false);
  }, [id, alert, t, dispatch, name, pw, gotoAuthReset]);

  const onClickSocialLogin = useCallback((socialType: Parameters<typeof postLoginBySocial>[0]) => async () => {
    const { data, error } = await postLoginBySocial(socialType);
    if (error) {
      if (error === 'user close') await alert(t(Message.로그인이취소되었습니다));
      // 탈퇴한 회원의 경우
      if (error.split(':')[0] === 'will be deleted at') {
        const deletedAt = new Date(Number(error.split(':')[1]));
        dispatch(actions.user.authModal.setDeletedAt(deletedAt));
        dispatch(actions.user.authModal.setEmail(data!.email));
        dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.WILL_BE_DELETED_ACCOUNT));
      }
    } else if (!data) {
      await alert(t(Message.로그인정보가없습니다));
    } else if (!data.email) {
      dispatch(actions.user.authModal.setConfirmCodeType('social'));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.AUTH_EMAIL));
    } else {
      dispatch(actions.user.authModal.setToken({ at: data.at, rt: data.rt }));
      dispatch(actions.user.authModal.setCurrentPage(EAuthModalPage.SUCCESS));
    }
  }, [dispatch, alert, t]);

  const handleOnKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onClickSignup();
    }
  }, [onClickSignup]);

  return (
    <Wrapper>
      <UpperDiv>
        <BasicButtonLabel>
          {`${t(Message.이미계정이있으신가요)}`}
        </BasicButtonLabel>
        <MobileLogo />
        <BasicButton
          className="signUpPage-login"
          onClick={gotoLogin}
        >
          {t(Message.로그인)}
        </BasicButton>
      </UpperDiv>
      <ContentDiv>
        {register === 'general' ? <Title className="signup">{ t(Message.회원가입)}</Title>
          : <Title>{t(Message.강사회원정보)}</Title> }
        <Input
          onChange={onChangeNameInput}
          placeholder={t(Message.닉네임을입력해주세요)}
          validate={isName}
          onBlur={onFocusOutName}
          onFocus={onFocusInName}
        />
        {/* TODO: 이름을 입력해주세요 모달 뜨는거 check */}
        { (nameModal !== '' && isName !== undefined) && <BalloonModal fade={isName} text={nameModal} />}

        <Input
          onChange={onChangeIdInput}
          placeholder={t(Message.이메일을입력하세요)}
          validate={isId}
          onBlur={onFocusOutId}
          onFocus={onFocusInId}
          autoComplete="email"
        />
        {(idModal !== '' && isId !== undefined) && <BalloonModal fade={isId} text={idModal} />}

        <Input
          onChange={onChangePwInput}
          placeholder={t(Message.비밀번호를입력하세요)}
          validate={isPw}
          onBlur={onFocusOutPw}
          onFocus={onFocusInPw}
          type="password"
          autoComplete="password"
        />
        {(pwModal && isPw !== undefined) && <BalloonModal fade={isPw} text={pwModal} />}

        <Input
          onChange={onChangeConfirmInput}
          onKeyPress={handleOnKeyPress}
          placeholder={t(Message.비밀번호확인)}
          type="password"
          validate={isConfirm} // 여기 다시 check
        />
        {isConfirm && <BalloonModal fade={isConfirm} text={t(Message.비밀번호와같지않습니다)} />}

        <Container className="signup">
          <Button
            type="button"
            onClick={register === 'general' ? gotoRegister : gotoCreator}
            active={false}
          >
            {t(Message.이전)}
          </Button>
          <NextButton
            type="button"
            onClick={onClickSignup}
            disabled={!isValid}
            active={isValid}
          >
            {t(Message.다음)}
          </NextButton>
        </Container>
        <MobileSocialLoginTitle>
          <Line />
          <SimpleLoginTitle>{t(Message.간편회원가입)}</SimpleLoginTitle>
          <Line />
        </MobileSocialLoginTitle>
        <Container className="social-login">
          <RoundButton onClick={onClickSocialLogin('google')}>
            <ButtonLogo src="/assets/images/auth/google-logo.png" alt="google" />
            <div>{t(Message.Google로계속하기)}</div>
          </RoundButton>
          <RoundButton onClick={onClickSocialLogin('facebook')}>
            <ButtonLogo src="/assets/images/auth/facebook-logo.png" alt="facebook" />
            <div>{t(Message.Facebook으로계속하기)}</div>
          </RoundButton>
        </Container>
      </ContentDiv>
    </Wrapper>

  );
};

interface ButtonProps {
  active?: boolean;
}

const Button = styled(BasicButton)<ButtonProps>`
  padding: 16px 74px;
  flex: 0.5;
  background-color: ${(props) => (props.active ? theme.color.purple : '#4B586A')};
  ${media.lg`
    padding: 16px 0;
    flex: 1;
  `}
`;

const NextButton = styled(Button)`
  background-color: ${theme.color.purple};
  :disabled{
    background-color: #F0F0F0;
    color: ${theme.color.darkGray};
  }
`;

const UpperDiv = styled(ButtonDiv)`
  padding-bottom: 33px;
  ${media.lg`
    padding-bottom: 50px;
  `}
`;
